import { useCallback, SetStateAction } from 'react';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import { BlacklistRequest } from '../../../../store/reducers/BlacklistSlice';
import { RangeValue } from './types';
import { formatDate } from '../../../../utils';
import { useBlacklistHandlersFilter } from './useBlacklistHandlersFilter/useBlacklistHandlersFilter';

interface Props {
  onIndex: (value: SetStateAction<number | undefined>) => void;
  onCallRequest: (value: BlacklistRequest) => void;
  callRequest: BlacklistRequest;
  onPageSize: (value: SetStateAction<number>) => void;
  total: number;
}

export const useBlacklistHandlers = ({
  onIndex,
  onCallRequest,
  callRequest,
  onPageSize,
  total,
}: Props) => {
  const { handleReasonChange, reasonValue, handleOpenReason } =
    useBlacklistHandlersFilter({
      onCallRequest,
      callRequest,
    });

  const handleSearch = useCallback(
    (value: string) => {
      onCallRequest({
        ...callRequest,
        TextSearch: value,
      });
    },
    [onCallRequest, callRequest],
  );
  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      if (!values || !formatString[0] || !formatString[1]) {
        const today = dayjs().format('YYYY-MM-DD');
        onCallRequest({
          ...callRequest,
          From: today,
          To: today,
        });
        return;
      }

      onCallRequest({
        ...callRequest,
        From: formatDate(formatString[0]),
        To: formatDate(formatString[1]),
      });
    },
    [onCallRequest, callRequest],
  );

  const handleChange = useCallback(
    (page: number, pageItemCount: number) => {
      onPageSize(pageItemCount);
      onCallRequest({
        ...callRequest,
        PageIndex: page - 1,
        PageSize: pageItemCount,
      });
      onIndex(undefined);
    },
    [onCallRequest, callRequest, onPageSize, onIndex],
  );

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const page =
        callRequest.PageIndex <= Math.floor(total / Number(value))
          ? callRequest.PageIndex
          : Math.floor(total / Number(value));
      onPageSize(Number(value));
      onCallRequest({
        ...callRequest,
        PageIndex: page,
        PageSize: Number(value),
      });
      onIndex(undefined);
    },
    [callRequest, total, onPageSize, onCallRequest, onIndex],
  );

  return {
    handleReasonChange,
    reasonValue,
    handleOpenReason,
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
  };
};
